﻿import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useResource, Feedback } from "@/components/workflow/shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SignaturePad } from "@/components/oprema/PrimopredajaStep";
import { danas } from "@/lib/dan";
import { datum, iznos } from "@/lib/format";
import {
  equipmentE2Api,
  type E2Result,
  type EquipmentLookup,
  type Rental,
  type Bucket,
} from "@/lib/api/oprema-e2";
import {
  Field,
  Photos,
  Quantity,
  useEquipmentAccess,
  conditionNames,
} from "./shared";
import {
  emptyProof,
  HandoverProofFields,
  proofInput,
  proofReady,
  proofMissing,
} from "./HandoverProof";
import { E2DocumentActions, E2Errors, E2Notice, useE2Save } from "./E2Shared";
import { EquipmentDetail } from "./EquipmentList";
import { useEquipmentNavigation } from "./EquipmentNavigation";
import { EquipmentDraft, useEquipmentDraftState, type EquipmentDraftCache } from "./EquipmentDraft";

type RentalAction = "return" | "extend" | "damage";

export function RentalViews({ lookup }: { lookup: EquipmentLookup }) {
  const access = useEquipmentAccess();
  const navigation = useEquipmentNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const [drafts] = useState<EquipmentDraftCache>(() => new Map());
  const selected = navigation.params.get("najam") ?? "";
  const action = navigation.params.get("radnja");
  const newRental = action === "new";
  const placeId = navigation.params.get("mjesto");
  const resource = useResource(
    "e2-rentals",
    useCallback((signal) => equipmentE2Api.rentals(signal), []),
  );
  const suppliers = useResource(
    `e2-suppliers:${access.maintain}`,
    useCallback(
      (signal) => access.maintain ? equipmentE2Api.suppliers(signal) : Promise.resolve([]),
      [access.maintain],
    ),
  );
  const [operation, setOperation] = useState<E2Result | null>(null);
  const open = (identifier: string, nextAction: string | null = null, replace = false) => {
    const params = new URLSearchParams(navigation.params);
    if (identifier) params.set("najam", identifier);
    else params.delete("najam");
    if (nextAction) params.set("radnja", nextAction);
    else params.delete("radnja");
    navigate({ pathname: location.pathname, search: params.toString() }, {
      replace,
      state: { ...location.state, equipmentOpened: location.pathname },
    });
  };
  const closeAction = () => {
    if (location.state?.equipmentOpened === location.pathname) navigate(-1);
    else open(selected, null, true);
  };
  const closeDetail = () => navigation.close(["najam", "radnja"]);
  const saved = (result: E2Result) => {
    setOperation(result);
    drafts.delete(`${selected}:${action}`);
    if (action && !newRental && location.state?.equipmentOpened === location.pathname) navigate(-1);
    else open(result.najam_id ?? selected, null, true);
    resource.reload();
  };
  const rows = resource.data?.filter((row) => !placeId ||
    row.stanje.some((line) => line.mjesto_id === placeId &&
      line.ispravno + line.osteceno + line.u_radioni > 0) ||
    row.komadi.some((piece) => piece.mjesto_id === placeId &&
      ["ispravno", "osteceno", "u_radioni"].includes(piece.stanje)));
  const rental = rows?.find((row) => row.id === selected);
  const singleReturn = placeId && action === "return" && !selected && rows?.length === 1
    ? rows[0]?.id : null;
  useEffect(() => {
    if (!singleReturn) return;
    const params = new URLSearchParams(location.search);
    params.set("najam", singleReturn);
    navigate({ pathname: location.pathname, search: params.toString() }, {
      replace: true,
      state: location.state,
    });
  }, [singleReturn, location.pathname, location.search, location.state, navigate]);
  return (
    <section className="site-equipment" aria-label="Najam">
      <div className="equipment-row">
        <h2 className="text-xl font-bold">Najam</h2>
        {access.maintain && <Button onClick={() => open("", "new")}>Primitak u najam</Button>}
      </div>
      <Feedback error={resource.error || suppliers.error} />
      {resource.loading && <p role="status">Učitavanje najma…</p>}
      {!rental && !newRental && <E2Notice result={operation} message="Zapisnik najma spremljen." onUndone={() => {
        setOperation(null); resource.reload();
      }} />}
      <div className="equipment-place-list">
        {rows?.map((row) => <Button
          key={row.id}
          variant="outline"
          className="equipment-placecard"
          aria-label={`Otvori najam: ${row.najmodavac} · ${row.broj_ugovora}`}
          onClick={() => open(row.id, action === "return" ? "return" : null)}
        >
          <span className="equipment-list-name"><b>{row.najmodavac}</b><small>{row.broj_ugovora} · {row.vani} kom vani</small></span>
          <Badge tone={row.status === "zatvoren" ? "neutral" : row.dana_do_povrata < 0 ? "bad" : row.dana_do_povrata <= 7 ? "warn" : "info"}>
            {row.status === "zatvoren" ? "Zatvoren" : `Povrat do ${datum(row.povrat_do)}`}
          </Badge>
          <span aria-hidden="true">›</span>
        </Button>)}
      </div>
      {rows?.length === 0 && <p>Nema evidentiranog najma za ovaj prikaz.</p>}
      {newRental && access.maintain && <EquipmentDetail title="Primitak u najam" crumbs={[{ label: "Najam", to: "/oprema/najam" }, { label: "Primitak" }]} onClose={closeAction}>
        <EquipmentDraft cache={drafts} name=":new"><RentalReceipt lookup={lookup} suppliers={suppliers.data ?? []} onSaved={saved} onClose={closeAction} /></EquipmentDraft>
      </EquipmentDetail>}
      {rental && !newRental && <EquipmentDetail title={`${rental.najmodavac} · ${rental.broj_ugovora}`} crumbs={[{ label: "Najam", to: "/oprema/najam" }, { label: rental.broj_ugovora }]} onClose={action ? closeAction : closeDetail}>
        <E2Notice result={operation} message="Zapisnik najma spremljen." onUndone={() => { setOperation(null); resource.reload(); }} />
        <EquipmentDraft key={`${selected}:${action}`} cache={drafts} name={`${selected}:${action}`}>
          <RentalDetail rental={rental} lookup={lookup} onSaved={saved} reload={resource.reload}
            form={access.maintain && (action === "return" || action === "extend" || action === "damage") ? action : null}
            setForm={(next) => next ? open(rental.id, next) : closeAction()} />
        </EquipmentDraft>
      </EquipmentDetail>}
    </section>
  );
}

export function CounterpartyProof({
  name,
  setName,
  signature,
  setSignature,
}: {
  name: string;
  setName: (name: string) => void;
  signature: File | null;
  setSignature: (file: File | null) => void;
}) {
  return (
    <details className="equipment-card">
      <summary className="equipment-link">
        Potpis najmodavca/vozača (neobavezno)
      </summary>
      <Field label="Ime najmodavca/vozača">
        <input
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
          required={!!signature}
        />
      </Field>
      <SignaturePad label="Potpis najmodavca/vozača" onChange={setSignature} />
      {signature && !name.trim() && (
        <p className="equipment-error">
          Uz drugi potpis upišite ime najmodavca/vozača.
        </p>
      )}
    </details>
  );
}

function RentalReceipt({
  lookup,
  suppliers,
  onSaved,
  onClose,
}: {
  lookup: EquipmentLookup;
  suppliers: { id: string; naziv: string }[];
  onSaved: (r: E2Result) => void;
  onClose: () => void;
}) {
  const access = useEquipmentAccess(),
    save = useE2Save();
  const [supplier, setSupplier] = useEquipmentDraftState("supplier", ""),
    [contract, setContract] = useEquipmentDraftState("contract", ""),
    [place, setPlace] = useEquipmentDraftState("place", "");
  const [received, setReceived] = useEquipmentDraftState("received", danas),
    [deadline, setDeadline] = useEquipmentDraftState("deadline", danas),
    [note, setNote] = useEquipmentDraftState("note", "");
  const [price, setPrice] = useEquipmentDraftState("price", ""),
    [priceKind, setPriceKind] = useEquipmentDraftState<"dan" | "mjesec">("priceKind", "dan");
  const [lines, setLines] = useEquipmentDraftState("lines", [
    { artikl_id: "", kolicina: 1, inventarni_broj: "", serijski_broj: "" },
  ]);
  const [photos, setPhotos] = useEquipmentDraftState<File[]>("photos", []),
    [proof, setProof] = useEquipmentDraftState("proof", emptyProof),
    [second, setSecond] = useEquipmentDraftState<File | null>("second", null),
    [name, setName] = useEquipmentDraftState("name", "");
  const valid =
    !!supplier &&
    !!contract.trim() &&
    !!place &&
    deadline >= received &&
    lines.every(
      (s) =>
        !!s.artikl_id &&
        Number.isInteger(s.kolicina) &&
        s.kolicina > 0 &&
        (lookup.articles.find((a) => a.id === s.artikl_id)?.nacin !== "komad" ||
          s.kolicina === 1),
    ) &&
    proofReady(photos, proof, access.evidenceException) &&
    (!second || !!name.trim());
  const blocked = !supplier ? "Odaberi najmodavca"
    : !contract.trim() ? "Upiši broj ugovora"
    : lines.some((line) => !line.artikl_id) ? "Odaberi opremu"
    : lines.some((line) => !Number.isInteger(line.kolicina) || line.kolicina <= 0 ||
        (lookup.articles.find((article) => article.id === line.artikl_id)?.nacin === "komad" && line.kolicina !== 1)) ? "Upiši količinu"
    : !place ? "Odaberi odredište"
    : !received || !deadline || deadline < received ? "Upiši rok povrata"
    : second && !name.trim() ? "Upiši ime uz drugi potpis"
    : proofMissing(photos, proof, access.evidenceException);
  const patch = (index: number, data: Partial<(typeof lines)[number]>) =>
    setLines((old) => old.map((s, i) => (i === index ? { ...s, ...data } : s)));
  return (
    <form
      className="equipment-form"
      aria-label="Primitak u najam"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || blocked) return;
        void save.run(
          () =>
            equipmentE2Api.receiveRental(
              {
                dobavljac_id: supplier,
                broj_ugovora: contract.trim(),
                u_mjesto_id: place,
                datum: received,
                datum_preuzimanja: received,
                povrat_do: deadline,
                napomena: note || null,
                cijena_dan: priceKind === "dan" && price ? price : null,
                cijena_mjesec: priceKind === "mjesec" && price ? price : null,
                stavke: lines.map((s) => ({
                  ...s,
                  inventarni_broj: s.inventarni_broj || null,
                  serijski_broj: s.serijski_broj || null,
                })),
                drugi_potpis_ime: name || null,
                ...proofInput(photos, proof, access.evidenceException),
              },
              { photos, signature: proof.potpis, second },
            ),
          onSaved,
        );
      }}
    >
      <fieldset className={`equipment-step ${supplier && contract.trim() && lines.every((line) => line.artikl_id && line.kolicina > 0) ? "is-done" : ""}`}>
        <legend><span className="equipment-step-number">1</span> Što primaš</legend>
      <Field label="Najmodavac" error={save.fields.dobavljac_id}>
        <select
          required
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
        >
          <option value="">Odaberi najmodavca</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.naziv}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Broj ugovora / otpremnice" error={save.fields.broj_ugovora}>
        <input
          required
          maxLength={200}
          value={contract}
          onChange={(e) => setContract(e.target.value)}
        />
      </Field>
      {lines.map((line, index) => {
        const article = lookup.articles.find((a) => a.id === line.artikl_id);
        return (
          <div className="equipment-card" key={index}>
            <Field label={`Artikl ${index + 1}`}>
              <select
                required
                value={line.artikl_id}
                onChange={(e) =>
                  patch(index, {
                    artikl_id: e.target.value,
                    kolicina: 1,
                    inventarni_broj: "",
                    serijski_broj: "",
                  })
                }
              >
                <option value="">Odaberi opremu</option>
                {lookup.articles
                  .filter((a) => a.aktivna)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.naziv}
                    </option>
                  ))}
              </select>
            </Field>
            <Quantity
              label={`Količina ${index + 1}`}
              value={line.kolicina}
              min={1}
              max={article?.nacin === "komad" ? 1 : undefined}
              onChange={(kolicina) => patch(index, { kolicina })}
            />
            {article?.nacin === "komad" && (
              <>
                <Field
                  label={`Inventarni broj najmodavca ${index + 1} (neobavezno)`}
                >
                  <input
                    maxLength={100}
                    value={line.inventarni_broj}
                    onChange={(e) =>
                      patch(index, { inventarni_broj: e.target.value })
                    }
                  />
                </Field>
                <Field label={`Serijski broj ${index + 1} (neobavezno)`}>
                  <input
                    maxLength={200}
                    value={line.serijski_broj}
                    onChange={(e) =>
                      patch(index, { serijski_broj: e.target.value })
                    }
                  />
                </Field>
              </>
            )}
            {lines.length > 1 && (
              <Button
                variant="outline"
                type="button"
                onClick={() =>
                  setLines((old) => old.filter((_, i) => i !== index))
                }
              >
                Ukloni stavku {index + 1}
              </Button>
            )}
          </div>
        );
      })}
      <Button
        variant="outline"
        type="button"
        onClick={() =>
          setLines((old) => [
            ...old,
            {
              artikl_id: "",
              kolicina: 1,
              inventarni_broj: "",
              serijski_broj: "",
            },
          ])
        }
      >
        Dodaj stavku
      </Button>
      </fieldset>
      <fieldset className={`equipment-step ${place && deadline >= received ? "is-done" : ""}`}>
        <legend><span className="equipment-step-number">2</span> Kamo i do kada</legend>
      <Field label="Odredište" error={save.fields.u_mjesto_id}>
        <select
          required
          value={place}
          onChange={(e) => setPlace(e.target.value)}
        >
          <option value="">Odaberi gradilište ili Bazu</option>
          {lookup.places
            .filter(
              (p) =>
                p.aktivno &&
                p.vrsta !== "radiona" &&
                access.canMaintain(p) &&
                (access.all || !!p.gradiliste_id),
            )
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.naziv}
              </option>
            ))}
        </select>
      </Field>
      <Field label="Datum preuzimanja">
        <input
          required
          type="date"
          max={danas()}
          value={received}
          onChange={(e) => setReceived(e.target.value)}
        />
      </Field>
      <Field label="Povrat do" error={save.fields.povrat_do}>
        <input
          required
          type="date"
          min={received}
          value={deadline}
          onChange={(e) => setDeadline(e.target.value)}
        />
      </Field>
      <Field label="Cijena (neobavezno, informativno)">
        <input
          inputMode="decimal"
          pattern="\d+(\.\d{1,2})?"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
      </Field>
      <Field label="Cijena po">
        <select
          value={priceKind}
          onChange={(e) => setPriceKind(e.target.value as "dan" | "mjesec")}
        >
          <option value="dan">Danu</option>
          <option value="mjesec">Mjesecu</option>
        </select>
      </Field>
      <Field label="Napomena (neobavezno)">
        <textarea
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      </fieldset>
      <fieldset className={`equipment-step ${proofReady(photos, proof, access.evidenceException) && (!second || name.trim()) ? "is-done" : ""}`}>
        <legend><span className="equipment-step-number">3</span> Fotka i potpis</legend>
      <Photos
        files={photos}
        onChange={setPhotos}
        required
        error={save.fields.dokumenti}
      />
      <HandoverProofFields
        value={proof}
        onChange={setProof}
        label="Primio"
        photos={photos}
        allowException={access.evidenceException}
        error={save.fields.potpis_id}
      />
      <CounterpartyProof
        name={name}
        setName={setName}
        signature={second}
        setSignature={setSecond}
      />
      </fieldset>
      <E2Errors save={save} />
      <Button type="submit" disabled={!valid || !!blocked || save.busy}>
        {save.busy ? "Spremam…" : blocked || "Potvrdi primitak u najam"}
      </Button>
      <Button variant="outline" type="button" onClick={onClose}>
        Odustani
      </Button>
    </form>
  );
}

function RentalDetail({
  rental,
  lookup,
  onSaved,
  reload,
  form,
  setForm,
}: {
  rental: Rental;
  lookup: EquipmentLookup;
  onSaved: (r: E2Result) => void;
  reload: () => void;
  form: RentalAction | null;
  setForm: (value: RentalAction | null) => void;
}) {
  const navigate = useNavigate();
  const access = useEquipmentAccess(),
    save = useE2Save();
  const { user } = useAuth();
  const readAll =
    user?.permissions.some(
      (p) =>
        p.module === "oprema_gradiliste" &&
        p.action === "read" &&
        p.scope === "all",
    ) ?? false;
  const [deadline, setDeadline] = useEquipmentDraftState("deadline", rental.povrat_do),
    [reason, setReason] = useEquipmentDraftState("reason", "");
  return (
    <div className="site-equipment">
      <Badge tone={rental.status === "zatvoren" ? "ok" : rental.dana_do_povrata <= 7 ? "warn" : "info"}>{rental.status === "zatvoren" ? "Zatvoren" : rental.status === "djelomicno_vracen" ? "Djelomično vraćen" : "U najmu"}</Badge>
      <div className="equipment-summary">
        <div><b>{rental.vani}</b><span>kom vani</span></div>
        <div><b>{rental.dani_u_najmu}</b><span>dana u najmu</span></div>
      </div>
      {rental.informativni_trosak !== null && <p>Informativni trošak: {iznos(rental.informativni_trosak)} €</p>}
      {rental.cijena_mjesec !== null && <p>Cijena po mjesecu: {iznos(rental.cijena_mjesec)} € (informativno, razmjerno danima / 30)</p>}
      <p>
        Preuzeto {datum(rental.datum_preuzimanja)}
        {rental.napomena ? ` · ${rental.napomena}` : ""}
      </p>
      <h3 className="font-bold">Gdje je oprema</h3>
      {(rental.u_prijevozu ?? []).map((line, index) => {
        const destination = lookup.places.find(
          (place) => place.id === line.u_mjesto_id,
        );
        const visibleDestination = readAll || !!destination?.gradiliste_id;
        return (
          <p
            className="equipment-warning"
            key={`${line.selidba_id}:${line.artikl_id}:${line.komad_id ?? index}`}
          >
            U prijevozu:{" "}
            {lookup.articles.find((article) => article.id === line.artikl_id)
              ?.naziv ?? "Oprema"}{" "}
            · {line.kolicina} kom · {line.iz_mjesta} →{" "}
            {visibleDestination ? (
              <Button variant="outline" onClick={() => navigate(`/oprema/mjesta/${line.u_mjesto_id}`)}>
                {line.u_mjesto}
              </Button>
            ) : (
              line.u_mjesto
            )}
          </p>
        );
      })}
      {rental.stanje
        .filter((b) => b.ispravno + b.osteceno + b.u_radioni > 0)
        .map((b) => (
          <p key={b.id}>
            {lookup.articles.find((a) => a.id === b.artikl_id)?.naziv ??
              "Oprema"}{" "}
            ·{" "}
            <Button variant="outline" onClick={() => navigate(`/oprema/mjesta/${b.mjesto_id}`)}>
              {lookup.places.find((p) => p.id === b.mjesto_id)?.naziv ??
                "Gradilište"}
            </Button>{" "}
            · {b.ispravno} ispravno / {b.osteceno} oštećeno / {b.u_radioni} u
            radioni
          </p>
        ))}
      {rental.komadi.map((p) => (
        <p key={p.id}>
          <Button variant="outline" onClick={() => navigate(`/oprema/komadi/${p.id}`)}>{p.inventarni_broj}</Button> ·{" "}
          {lookup.places.find((x) => x.id === p.mjesto_id)?.naziv ??
            "Gradilište"}{" "}
          · {conditionNames[p.stanje]}
        </p>
      ))}
      {access.maintain && !form && (
        <div className="equipment-place-actions">
          {rental.status !== "zatvoren" && (
            <>
              <Button onClick={() => setForm("return")}>
                Vrati najmodavcu
              </Button>
              <Button variant="outline" onClick={() => setForm("extend")}>
                Produži rok
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => setForm("damage")}>
            Šteta prema najmodavcu
          </Button>
        </div>
      )}
      {form === "return" && (
        <RentalReturnForm
          rental={rental}
          lookup={lookup}
          onSaved={onSaved}
          onClose={() => setForm(null)}
        />
      )}
      {form === "damage" && (
        <RentalDamageForm
          rental={rental}
          lookup={lookup}
          onSaved={onSaved}
          onClose={() => setForm(null)}
        />
      )}
      {form === "extend" && (
        <form
          className="equipment-form"
          aria-label="Produži rok najma"
          onSubmit={(e) => {
            e.preventDefault();
            void save.run(
              () =>
                equipmentE2Api.extendRental(rental.id, deadline, reason.trim()),
              (result) => {
                onSaved(result);
                reload();
              },
            );
          }}
        >
          <fieldset className={`equipment-step ${deadline > rental.povrat_do ? "is-done" : ""}`}>
          <legend><span className="equipment-step-number">1</span> Novi rok</legend>
          <Field label="Novi rok" error={save.fields.povrat_do}>
            <input
              type="date"
              required
              min={rental.povrat_do}
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </Field>
          </fieldset>
          <fieldset className={`equipment-step ${reason.trim() ? "is-done" : ""}`}>
          <legend><span className="equipment-step-number">2</span> Razlog</legend>
          <Field label="Razlog produljenja">
            <textarea
              required
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          </fieldset>
          <E2Errors save={save} />
          <Button
            type="submit"
            disabled={
              save.busy || deadline <= rental.povrat_do || !reason.trim()
            }
          >
            {save.busy ? "Spremam…" : deadline <= rental.povrat_do ? "Upiši kasniji rok" : !reason.trim() ? "Upiši razlog produljenja" : "Spremi novi rok"}
          </Button>
        </form>
      )}
      <details>
        <summary className="equipment-link">Dnevnik najma</summary>
        {rental.produljenja.map((e) => (
          <p key={e.id}>
            {datum(e.stari_rok)} → {datum(e.novi_rok)} · {e.razlog}
              {e.ponisteno ? " · Poništeno" : ""}
          </p>
        ))}
        {rental.dokumenti.map((d) => (
          <E2DocumentActions key={d.id} document={d} />
        ))}
      </details>
    </div>
  );
}

export function RentalReturnForm({
  rental,
  lookup,
  onSaved,
  onClose,
}: {
  rental: Rental;
  lookup: EquipmentLookup;
  onSaved: (r: E2Result) => void;
  onClose: () => void;
}) {
  const access = useEquipmentAccess(),
    save = useE2Save();
  const [counts, setCounts] = useEquipmentDraftState<Record<string, number>>("counts", {}),
    [photos, setPhotos] = useEquipmentDraftState<File[]>("photos", []),
    [proof, setProof] = useEquipmentDraftState("proof", emptyProof),
    [second, setSecond] = useEquipmentDraftState<File | null>("second", null),
    [name, setName] = useEquipmentDraftState("name", "");
  const targets = [
    ...rental.stanje.flatMap((s) =>
      (["ispravno", "osteceno", "u_radioni"] as Bucket[])
        .filter((bucket) => s[bucket] > 0)
        .map((bucket) => ({
          key: `${s.id}:${bucket}`,
          artikl_id: s.artikl_id,
          komad_id: null as string | null,
          mjesto_id: s.mjesto_id,
          stanje: bucket,
          max: s[bucket],
          label: `${lookup.articles.find((a) => a.id === s.artikl_id)?.naziv ?? "Oprema"} · ${lookup.places.find((p) => p.id === s.mjesto_id)?.naziv ?? "Gradilište"} · ${conditionNames[bucket]}`,
        })),
    ),
    ...rental.komadi
      .filter((p) => ["ispravno", "osteceno", "u_radioni"].includes(p.stanje))
      .map((p) => ({
        key: p.id,
        artikl_id: p.artikl_id,
        komad_id: p.id as string | null,
        mjesto_id: p.mjesto_id,
        stanje: p.stanje as Bucket,
        max: 1,
        label: `${p.inventarni_broj} · ${lookup.places.find((x) => x.id === p.mjesto_id)?.naziv ?? "Gradilište"} · ${conditionNames[p.stanje]}`,
      })),
  ].filter((t) => {
    const place = lookup.places.find((p) => p.id === t.mjesto_id);
    return !!place && access.canMaintain(place);
  });
  const selected = targets.filter((t) => (counts[t.key] ?? 0) > 0);
  const valid =
    selected.length > 0 &&
    targets.every(
      (t) =>
        Number.isInteger(counts[t.key] ?? 0) &&
        (counts[t.key] ?? 0) >= 0 &&
        (counts[t.key] ?? 0) <= t.max,
    ) &&
    proofReady(photos, proof, access.evidenceException) &&
    (!second || !!name.trim());
  return (
    <form
      className="equipment-form"
      aria-label="Vrati najmodavcu"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        void save.run(
          () =>
            equipmentE2Api.returnRental(
              rental.id,
              {
                datum: danas(),
                stavke: selected.map((t) => ({
                  artikl_id: t.artikl_id,
                  komad_id: t.komad_id,
                  mjesto_id: t.mjesto_id,
                  stanje: t.stanje,
                  kolicina: counts[t.key],
                })),
                drugi_potpis_ime: name || null,
                ...proofInput(photos, proof, access.evidenceException),
              },
              { photos, signature: proof.potpis, second },
            ),
          onSaved,
        );
      }}
    >
      <h3 className="font-bold">Vrati najmodavcu · {rental.najmodavac}</h3>
      <fieldset className={`equipment-step ${selected.length ? "is-done" : ""}`}>
        <legend><span className="equipment-step-number">1</span> Što vraćaš</legend>
      {targets.map((t) => (
        <div className="equipment-transfer-line" key={t.key}>
          <b>{t.label}</b>
          <Quantity
            label={`Vrati: ${t.label}`}
            max={t.max}
            value={counts[t.key] ?? 0}
            onChange={(n) => setCounts((old) => ({ ...old, [t.key]: n }))}
          />
        </div>
      ))}
      {!targets.length && (
        <p>
          Nema opreme dostupne za povrat. Opremu u prijevozu prvo treba primiti.
        </p>
      )}
      </fieldset>
      <fieldset className={`equipment-step ${proofReady(photos, proof, access.evidenceException) && (!second || name.trim()) ? "is-done" : ""}`}>
        <legend><span className="equipment-step-number">2</span> Fotka i potpis</legend>
      <Photos files={photos} onChange={setPhotos} required />
      <HandoverProofFields
        value={proof}
        onChange={setProof}
        label="Predao"
        photos={photos}
        allowException={access.evidenceException}
      />
      <CounterpartyProof
        name={name}
        setName={setName}
        signature={second}
        setSignature={setSecond}
      />
      </fieldset>
      <E2Errors save={save} />
      <Button type="submit" disabled={save.busy || !valid}>
        {save.busy ? "Spremam…" : !selected.length ? "Odaberi što vraćaš" : !proofReady(photos, proof, access.evidenceException) ? proofMissing(photos, proof, access.evidenceException) : second && !name.trim() ? "Upiši ime uz drugi potpis" : !valid ? "Upiši količinu" : "Potvrdi povrat najmodavcu"}
      </Button>
      <Button variant="outline" type="button" onClick={onClose}>
        Odustani
      </Button>
    </form>
  );
}

function RentalDamageForm({
  rental,
  lookup,
  onSaved,
  onClose,
}: {
  rental: Rental;
  lookup: EquipmentLookup;
  onSaved: (r: E2Result) => void;
  onClose: () => void;
}) {
  const save = useE2Save();
  const [article, setArticle] = useEquipmentDraftState("article", ""),
    [piece, setPiece] = useEquipmentDraftState("piece", ""),
    [place, setPlace] = useEquipmentDraftState("place", ""),
    [quantity, setQuantity] = useEquipmentDraftState("quantity", 1),
    [description, setDescription] = useEquipmentDraftState("description", ""),
    [amount, setAmount] = useEquipmentDraftState("amount", ""),
    [photos, setPhotos] = useEquipmentDraftState<File[]>("photos", []);
  const blocked = !article ? "Odaberi opremu"
    : lookup.articles.find((row) => row.id === article)?.nacin === "komad" && !piece ? "Odaberi komad"
    : !place ? "Odaberi mjesto"
    : !Number.isInteger(quantity) || quantity < 1 || (!!piece && quantity !== 1) ? "Upiši količinu"
    : !description.trim() ? "Upiši opis štete"
    : !photos.length ? "Dodaj fotku" : "";
  return (
    <form
      className="equipment-form"
      aria-label="Šteta prema najmodavcu"
      onSubmit={(e) => {
        e.preventDefault();
        if (blocked) return;
        void save.run(
          () =>
            equipmentE2Api.damageRental(
              rental.id,
              {
                datum: danas(),
                artikl_id: article,
                komad_id: piece || null,
                mjesto_id: place,
                kolicina: quantity,
                opis: description.trim(),
                iznos: amount || null,
              },
              photos,
            ),
          onSaved,
        );
      }}
    >
      <p>
        Šteta ne skida opremu sa stanja. Oštećenu opremu prvo vrati kroz
        „Vrati najmodavcu” uz fotku i potpis, kao oštećenu. Ovdje upiši
        štetu za vraćene oštećene jedinice ili već evidentirani manjak.
      </p>
      <fieldset className={`equipment-step ${article && place && quantity > 0 ? "is-done" : ""}`}>
        <legend><span className="equipment-step-number">1</span> Oprema i mjesto</legend>
      <Field label="Oprema">
        <select
          required
          value={article}
          onChange={(e) => {
            setArticle(e.target.value);
            setPiece("");
          }}
        >
          <option value="">Odaberi opremu</option>
          {lookup.articles.map((a) => (
            <option key={a.id} value={a.id}>
              {a.naziv}
            </option>
          ))}
        </select>
      </Field>
      {lookup.articles.find((a) => a.id === article)?.nacin === "komad" && (
        <Field label="Komad">
          <select
            required
            value={piece}
            onChange={(e) => {
              setPiece(e.target.value);
              setQuantity(1);
              const p = rental.komadi.find((p) => p.id === e.target.value);
              if (p) setPlace(p.mjesto_id);
            }}
          >
            <option value="">Odaberi komad</option>
            {rental.komadi
              .filter((p) => p.artikl_id === article)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.inventarni_broj}
                </option>
              ))}
          </select>
        </Field>
      )}
      <Field label="Mjesto štete">
        <select
          required
          value={place}
          onChange={(e) => setPlace(e.target.value)}
        >
          <option value="">Odaberi mjesto</option>
          {lookup.places.map((p) => (
            <option key={p.id} value={p.id}>
              {p.naziv}
            </option>
          ))}
        </select>
      </Field>
      <Quantity
        label="Količina štete"
        min={1}
        max={piece ? 1 : undefined}
        value={quantity}
        onChange={setQuantity}
      />
      </fieldset>
      <fieldset className={`equipment-step ${description.trim() ? "is-done" : ""}`}>
        <legend><span className="equipment-step-number">2</span> Opis štete</legend>
      <Field label="Opis štete">
        <textarea
          required
          maxLength={2000}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field label="Iznos (neobavezno, informativno)">
        <input
          inputMode="decimal"
          pattern="\d+(\.\d{1,2})?"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </Field>
      </fieldset>
      <fieldset className={`equipment-step ${photos.length ? "is-done" : ""}`}>
        <legend><span className="equipment-step-number">3</span> Fotka štete</legend>
      <Photos files={photos} onChange={setPhotos} required />
      </fieldset>
      <E2Errors save={save} />
      <Button
        type="submit"
        disabled={save.busy || !!blocked}
      >
        {save.busy ? "Spremam…" : blocked || "Spremi štetu"}
      </Button>
      <Button type="button" variant="outline" onClick={onClose}>
        Odustani
      </Button>
    </form>
  );
}
